import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Truck, Plane, Ship, Package, Shield, Clock, ArrowRight, CheckCircle } from 'lucide-react';

const Servicios = () => {
  const mainServices = [
    {
      id: 1,
      icon: Truck,
      title: "Transporte Terrestre",
      subtitle: "Rutas seguras y eficientes por carretera",
      description: "Contamos con una flota moderna de camiones especializados para el transporte de mercancías por toda América del Norte. Rutas directas México-Estados Unidos-Canadá con seguimiento en tiempo real.",
      features: ["Seguimiento GPS 24/7", "Seguro de mercancía incluido", "Entrega puerta a puerta", "Documentación completa"],
      color: "from-blue-500 to-blue-700",
      image: "https://utfs.io/f/5BN0V4mlt4NU0AqlEPeXK1e7fx2toMJsnUB0SuRjmzqgk85l"
    },
    {
      id: 2,
      icon: Plane,
      title: "Carga Aérea",
      subtitle: "Velocidad y precisión mundial",
      description: "Servicios de carga aérea express para envíos urgentes. Conectamos con las principales aerolíneas mundiales para garantizar entregas rápidas a cualquier destino internacional.",
      features: ["Entrega express 24-48h", "Manejo de carga especial", "Conexiones globales", "Tracking en tiempo real"],
      color: "from-green-500 to-green-700",
      image: "https://utfs.io/f/5BN0V4mlt4NUZTVVCyUTK6QzFRPgbcvS34JeBN2tCIwlfjUp"
    },
    {
      id: 3,
      icon: Ship,
      title: "Carga Marítima",
      subtitle: "Soluciones para grandes volúmenes",
      description: "Transporte marítimo internacional para cargas de gran volumen. Servicios FCL y LCL con conexiones directas a los principales puertos mundiales, especialmente Asia-Pacífico.",
      features: ["Contenedores FCL/LCL", "Rutas Asia-América", "Consolidación de carga", "Gestión portuaria"],
      color: "from-indigo-500 to-indigo-700",
      image: "https://images.unsplash.com/photo-1559827260-dc66d52bef19?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80"  // Nueva imagen de puerto de contenedores dinámica
    },
    {
      id: 4,
      icon: Package,
      title: "Almacenamiento",
      subtitle: "Espacios seguros para tu mercancía",
      description: "Centros de distribución estratégicamente ubicados con tecnología de punta. Almacenamiento temporal y permanente con sistemas de control de inventario automatizados.",
      features: ["Almacenes climatizados", "Control de inventario", "Cross-docking", "Empaque y re-empaque"],
      color: "from-purple-500 to-purple-700",
      image: "https://utfs.io/f/5BN0V4mlt4NU0VV7hzweXK1e7fx2toMJsnUB0SuRjmzqgk85"
    }
  ];

  const additionalServices = [
    {
      icon: Shield,
      title: "Seguros de Carga",
      description: "Protección completa para tu mercancía durante todo el trayecto"
    },
    {
      icon: Clock,
      title: "Servicios Express",
      description: "Entregas urgentes en tiempo récord para casos críticos"
    },
    {
      icon: CheckCircle,
      title: "Gestión Aduanal",
      description: "Trámites aduaneros completos y asesoría especializada"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <motion.section 
        className="bg-gradient-to-r from-green-800 to-green-900 py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-7xl mx-auto text-center">
          <motion.h1 
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Nuestros Servicios
          </motion.h1>
          <motion.p 
            className="text-xl sm:text-2xl text-gray-200 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Soluciones integrales de logística internacional diseñadas para conectar tu negocio con el mundo
          </motion.p>
        </div>
      </motion.section>

      <motion.section 
        className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="space-y-16 lg:space-y-24">
            {mainServices.map((service, index) => (
              <motion.div
                key={service.id}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 lg:gap-12 items-center`}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <div className="flex-1 lg:max-w-lg">
                  <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-shadow duration-300">
                    <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-2xl flex items-center justify-center mb-6`}>
                      <service.icon className="w-8 h-8 text-white" />
                    </div>
                    
                    <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{service.title}</h3>
                    <p className="text-lg text-green-600 font-semibold mb-4">{service.subtitle}</p>
                    <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                    
                    <div className="space-y-3 mb-8">
                      {service.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center space-x-3">
                          <CheckCircle className="w-5 h-5 text-green-500" />
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                    
                    <Link
                      to="/cotizacion"
                      className={`inline-flex items-center space-x-2 bg-gradient-to-r ${service.color} text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 transform hover:scale-105`}
                    >
                      <span>Solicitar Cotización</span>
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                  </div>
                </div>

                <div className="flex-1 lg:max-w-lg">
                  <motion.div
                    className="rounded-2xl overflow-hidden shadow-xl"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-64 sm:h-80 lg:h-96 object-cover"
                    />
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      <motion.section 
        className="py-16 sm:py-20 bg-white px-4 sm:px-6 lg:px-8"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Servicios Adicionales
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Complementa tu logística con nuestros servicios especializados
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {additionalServices.map((service, index) => (
              <motion.div
                key={index}
                className="bg-gray-50 p-8 rounded-2xl text-center hover:bg-white hover:shadow-lg transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <service.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      <motion.section 
        className="py-16 sm:py-20 bg-gradient-to-r from-green-800 to-green-900 px-4 sm:px-6 lg:px-8"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2 
            className="text-3xl sm:text-4xl font-bold text-white mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            ¿Necesitas un servicio personalizado?
          </motion.h2>
          <motion.p 
            className="text-xl text-gray-200 mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Contáctanos para diseñar una solución logística específica para tu negocio
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <Link
              to="/cotizacion"
              className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold px-8 py-4 rounded-full transition-all duration-300 transform hover:scale-105 inline-flex items-center justify-center space-x-2"
            >
              <span>Solicitar Cotización</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/contacto"
              className="border-2 border-white text-white hover:bg-white hover:text-green-900 font-bold px-8 py-4 rounded-full transition-all duration-300 transform hover:scale-105"
            >
              Contactar Experto
            </Link>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default Servicios;